import React, { useState, useRef, useCallback } from 'react';
import { tickerAPI } from '../../services/api';
import TickerQuickButtons from './TickerQuickButtons';
import TickerInlineAction from './TickerInlineAction';
import TickerInput from './TickerInput';
import TickerFeed from './TickerFeed';
import TickerSquadPanel from './TickerSquadPanel';
import TickerUndoToast from './TickerUndoToast';

export default function TickerTab({
    gameId,
    game,
    events,
    lineup,
    availablePlayers,
    meta,
    displayMinute,
    onCreated,
    onDelete,
    onUpdate,
    onMetaUpdate,
    onLineupChange,
    squadPanelOpen,
    onToggleSquadPanel,
}) {
    const composerRef = useRef(null);
    const gameFinished = meta?.status === 'finished';

    const [activeFlow, setActiveFlow] = useState(null);
    const [flowPreset, setFlowPreset] = useState(null);
    const [undo, setUndo] = useState(null);
    const [lastCreatedId, setLastCreatedId] = useState(null);

    const startFlow = useCallback((flow) => {
        if (flow === 'chance') {
            setActiveFlow(null);
            composerRef.current?.setChanceType?.();
            composerRef.current?.focus?.();
            return true;
        }
        setFlowPreset(null);
        setActiveFlow(flow);
        onToggleSquadPanel?.(true);
        return true;
    }, [onToggleSquadPanel]);

    const closeFlow = useCallback(() => {
        setActiveFlow(null);
        setFlowPreset(null);
    }, []);

    const registerUndo = useCallback((event, label) => {
        setLastCreatedId(event._id);
        setUndo({ event, label });
        setTimeout(() => setLastCreatedId(null), 2000);
    }, []);

    const handleCreated = useCallback((event) => {
        onCreated(event);
    }, [onCreated]);

    const handleUndo = useCallback(async () => {
        if (!undo?.event?._id) return;
        try {
            await tickerAPI.delete(undo.event._id);
            onDelete(undo.event._id);
        } catch (err) {
            console.error(err);
        } finally {
            setUndo(null);
        }
    }, [undo, onDelete]);

    const handleSquadPick = useCallback(({ playerKey, plid, teamId }) => {
        if (!activeFlow || activeFlow === 'chance') return;
        setFlowPreset({
            teamId,
            playerKey: playerKey || (plid ? `pl:${plid}` : null),
            step: activeFlow === 'sub' ? 'out' : 'player',
        });
    }, [activeFlow]);

    return (
        <div className={`ticker-tab-layout ${activeFlow ? 'ticker-tab-layout--flow-active' : ''}`}>
            <div className="ticker-tab-main">
                <div className="ticker-dock ticker-dock--top">
                    <TickerQuickButtons
                        onStartFlow={startFlow}
                        gameFinished={gameFinished}
                        activeFlow={activeFlow}
                    />

                    {activeFlow && ['goal', 'yellow', 'red', 'sub'].includes(activeFlow) && (
                        <TickerInlineAction
                            key={`${activeFlow}-${flowPreset?.teamId || ''}-${flowPreset?.playerKey || flowPreset?.plid || ''}`}
                            flow={activeFlow}
                            gameId={gameId}
                            game={game}
                            lineup={lineup}
                            events={events}
                            availablePlayers={availablePlayers}
                            meta={meta}
                            displayMinute={displayMinute}
                            onClose={closeFlow}
                            onCreated={handleCreated}
                            onMetaUpdate={onMetaUpdate}
                            onLineupChange={onLineupChange}
                            onUndoRegister={registerUndo}
                            preset={flowPreset}
                        />
                    )}

                    <TickerInput
                        ref={composerRef}
                        gameId={gameId}
                        currentMinute={displayMinute}
                        half={meta?.currentHalf || 1}
                        game={game}
                        lineup={lineup}
                        availablePlayers={availablePlayers}
                        onCreated={(event) => {
                            handleCreated(event);
                            registerUndo(event, 'Meldung');
                        }}
                        gameFinished={gameFinished}
                    />
                </div>

                <div className="ticker-feed-scroll">
                    <div className="ticker-feed-header">
                        <div>
                            <h2 className="text-sm font-bold text-slate-200">
                                {gameFinished ? 'Spielprotokoll · Nachspiel' : 'Live-Feed'}
                            </h2>
                            <p className="text-xs text-slate-500">
                                {events.filter((e) => e.published !== false).length} Meldungen · neueste oben
                            </p>
                        </div>
                        <button type="button" className="ticker-squad-toggle" onClick={onToggleSquadPanel}>
                            👥 Kader {squadPanelOpen ? '▾' : '▸'}
                        </button>
                    </div>
                    <TickerFeed
                        events={events}
                        onDelete={onDelete}
                        onUpdate={onUpdate}
                        gameFinished={gameFinished}
                        compact={!gameFinished}
                        lastCreatedId={lastCreatedId}
                        game={game}
                        lineup={lineup}
                        availablePlayers={availablePlayers}
                        meta={meta}
                        onLineupChange={onLineupChange}
                    />
                </div>
            </div>

            {squadPanelOpen && (
                <TickerSquadPanel
                    game={game}
                    lineup={lineup}
                    events={events}
                    availablePlayers={availablePlayers}
                    activeFlow={activeFlow}
                    onPlayerPick={handleSquadPick}
                    onClose={() => onToggleSquadPanel?.(false)}
                />
            )}

            {undo && (
                <TickerUndoToast
                    message={`${undo.label} gespeichert`}
                    onUndo={handleUndo}
                    onDismiss={() => setUndo(null)}
                />
            )}
        </div>
    );
}
